import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiPhone, FiArrowRight, FiCheck, FiAlertCircle, FiClock,
  FiFileText, FiUpload, FiSettings, FiMessageSquare, FiShield,
  FiGlobe, FiInfo, FiCheckCircle, FiXCircle, FiArrowLeft,
  FiExternalLink, FiDownload, FiDollarSign, FiRefreshCw, FiShoppingCart
} from 'react-icons/fi';
import { HiOutlineDocument, HiOutlineDocumentText } from 'react-icons/hi';
import { BsToggle2Off, BsToggle2On } from 'react-icons/bs';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { NumberSearchPanel } from '../components/PhoneNumbers/NumberSearchPanel';
import { ProvisioningModal } from '../components/PhoneNumbers/ProvisioningModal';
import { NumberManagementPanel } from '../components/PhoneNumbers/NumberManagementPanel';
import { AustralianCompliancePanel } from '../components/PhoneNumbers/AustralianCompliancePanel';

interface PortingStep {
  id: number;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed';
  estimatedTime?: string;
}

const PhoneNumbersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'search' | 'manage' | 'porting' | 'forwarding' | 'compliance'>('overview');
  const [selectedOption, setSelectedOption] = useState<'port' | 'forward' | null>(null);
  const [showPortingForm, setShowPortingForm] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [callRecording, setCallRecording] = useState(true);
  const [loading, setLoading] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [brands, setBrands] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<any>(null);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [selectedNumberForProvisioning, setSelectedNumberForProvisioning] = useState<any>(null);
  const [showProvisioningModal, setShowProvisioningModal] = useState(false);
  const navigate = useNavigate();

  const portingSteps: PortingStep[] = [
    {
      id: 1,
      title: 'Confirm Portability',
      description: 'Verify your number can be ported',
      status: 'completed',
      estimatedTime: 'Instant'
    },
    {
      id: 2,
      title: 'Gather Documents',
      description: 'Collect required paperwork',
      status: 'in-progress',
      estimatedTime: '1-2 hours'
    },
    {
      id: 3,
      title: 'Submit Request',
      description: 'Upload documents and submit',
      status: 'pending',
      estimatedTime: '15 minutes'
    },
    {
      id: 4,
      title: 'Processing',
      description: 'Carrier processing and approval',
      status: 'pending',
      estimatedTime: '7-10 business days'
    },
    {
      id: 5,
      title: 'Activation',
      description: 'Number active on platform',
      status: 'pending',
      estimatedTime: 'Automatic'
    }
  ];

  const requiredDocuments = [
    { name: 'Recent Phone Bill', description: 'PDF showing number and account owner', required: true },
    { name: 'Account Number', description: 'From your current carrier', required: true },
    { name: 'Port-out PIN', description: 'If required by your carrier', required: false },
    { name: 'Letter of Authorization', description: 'Signed LOA (we provide template)', required: true }
  ];

  const complianceRequirements = [
    { type: 'Business Registration', description: 'Register your business entity', status: 'completed' },
    { type: 'Brand Registration', description: 'Register with The Campaign Registry', status: 'pending' },
    { type: 'Campaign Creation', description: 'Define messaging use cases', status: 'pending' },
    { type: 'Number Association', description: 'Link numbers to campaigns', status: 'pending' }
  ];

  // Fetch user organization and phone numbers on mount
  useEffect(() => {
    fetchUserOrganization();
    fetchPhoneNumbers();
  }, []);

  const fetchUserOrganization = async () => {
    try {
      // Get user info from /auth/me endpoint
      const response = await api.get('/auth/me');
      const user = response.data.user;
      
      if (user?.organizationId) {
        setOrganizationId(user.organizationId);
        console.log('Organization ID loaded:', user.organizationId);
        return;
      }

      // If no organization, show error
      toast.error('No organization found for your account. Please contact support.');
      console.error('User has no organizationId:', user);
    } catch (error: any) {
      console.error('Failed to get organization:', error);
      
      // If auth endpoint fails, try fallback with JWT decode
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Not authenticated. Please log in.');
          return;
        }
        
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.organizationId) {
          setOrganizationId(payload.organizationId);
          console.log('Organization ID from JWT:', payload.organizationId);
        } else {
          toast.error('No organization found. Please contact support.');
        }
      } catch (jwtError) {
        console.error('Failed to decode JWT:', jwtError);
        toast.error('Failed to load user information');
      }
    }
  };

  const fetchPhoneNumbers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get('/phone-numbers');
      setPhoneNumbers(response.data.phoneNumbers || []);
      setStats(response.data.stats || {
        total: 0,
        active: 0,
        porting: 0,
        smsEnabled: 0,
        compliant: 0
      });
    } catch (error) {
      console.error('Error fetching phone numbers:', error);
      // Don't show error toast on initial load
    } finally {
      setLoading(false);
    }
  };

  const handleProvisionNumber = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/phone-numbers/provision',
        {
          areaCode: '415',
          capabilities: ['voice', 'sms'],
          friendlyName: 'New Business Line'
        }
      );
      
      toast.success('Phone number provisioned successfully!');
      fetchPhoneNumbers();
      return response.data.phoneNumber;
    } catch (error) {
      console.error('Error provisioning number:', error);
      toast.error('Failed to provision phone number');
    }
  };

  const handleStartPorting = async () => {
    try {
      let current = selectedPhoneNumber;
      if (!current) {
        const newNumber = await handleProvisionNumber();
        if (newNumber) {
          current = newNumber;
          setSelectedPhoneNumber(newNumber);
        }
      }
      if (!current) return;
      const resp = await api.post(`/phone-numbers/${current.id}/port`, {
        accountNumber: '123456',
        accountPin: '0000',
        billingName: 'Test User',
        billingAddress: { line1: '1 Test St', city: 'SF', state: 'CA', zip: '94105' },
        originalCarrier: 'Verizon'
      });
      setShowPortingForm(true);
      toast.success('Porting request created');
      fetchPhoneNumbers();
    } catch (e) {
      console.error('Start porting failed', e);
      toast.error('Failed to start porting');
    }
  };

  const handleSetupForwarding = async () => {
    try {
      let current = selectedPhoneNumber;
      if (!current) {
        const newNumber = await handleProvisionNumber();
        if (newNumber) {
          current = newNumber;
          setSelectedPhoneNumber(newNumber);
        }
      }
      if (!current) return;
      await api.put(`/phone-numbers/${current.id}/forwarding`, {
        forwardingEnabled: true,
        forwardingNumber: '+14155550100',
        forwardingRules: { strategy: 'simultaneous', targets: ['+14155550100'] }
      });
      toast.success('Call forwarding enabled');
      fetchPhoneNumbers();
    } catch (e) {
      console.error('Error updating forwarding:', e);
      toast.error('Failed to update forwarding');
    }
  };

  const handleCreateBrand = async (brandData: any) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/phone-numbers/compliance/brand',
        brandData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Brand registration initiated! Vetting process will take 24-48 hours.');
      setBrands([...brands, response.data.brand]);
    } catch (error) {
      console.error('Error creating brand:', error);
      toast.error('Failed to create brand registration');
    }
  };

  return (
    <div className="flex-1 p-6 bg-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Phone Numbers Management</h1>
          <p className="text-gray-400">Use your existing business numbers with full platform features</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-slate-800/50 p-1 rounded-lg overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: FiInfo },
            { id: 'search', label: 'Search & Buy', icon: FiShoppingCart },
            { id: 'manage', label: 'Manage Numbers', icon: FiSettings },
            { id: 'porting', label: 'Porting', icon: FiRefreshCw },
            { id: 'forwarding', label: 'Forwarding', icon: FiArrowRight },
            { id: 'compliance', label: 'Compliance', icon: FiShield }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 rounded-md transition-all flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              <tab.icon className="text-sm" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content based on active tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700"
              >
                <div className="flex items-center justify-between mb-4">
                  <FiPhone className="text-blue-400 text-2xl" />
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">Active</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">{stats?.active || 0}</h3>
                <p className="text-gray-400 text-sm">Active Numbers</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700"
              >
                <div className="flex items-center justify-between mb-4">
                  <FiMessageSquare className="text-purple-400 text-2xl" />
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full">10DLC</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">{stats?.smsEnabled || 0}</h3>
                <p className="text-gray-400 text-sm">SMS Enabled</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700"
              >
                <div className="flex items-center justify-between mb-4">
                  <FiClock className="text-orange-400 text-2xl" />
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full">Pending</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">{stats?.porting || 0}</h3>
                <p className="text-gray-400 text-sm">Port in Progress</p>
              </motion.div>
            </div>

            {/* Main Options */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Option 1: Port Number */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border-2 transition-all cursor-pointer ${
                  selectedOption === 'port' ? 'border-blue-500' : 'border-slate-700 hover:border-slate-600'
                }`}
                onClick={() => setSelectedOption('port')}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <FiRefreshCw className="text-blue-400 text-xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Option 1: Port Your Number</h3>
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">Recommended</span>
                    </div>
                  </div>
                  {selectedOption === 'port' && <FiCheckCircle className="text-blue-400 text-xl" />}
                </div>
                
                <p className="text-gray-400 mb-4">
                  Move your existing number to the platform for full features
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <FiCheck className="text-green-400" />
                    <span className="text-gray-300">Call and text from your number</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiCheck className="text-green-400" />
                    <span className="text-gray-300">Full automation and workflows</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiCheck className="text-green-400" />
                    <span className="text-gray-300">Complete analytics and tracking</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiCheck className="text-green-400" />
                    <span className="text-gray-300">Keep your existing number</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Timeline:</span>
                    <span className="text-white">7-10 business days</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-gray-400">Downtime:</span>
                    <span className="text-white">Minimal to none</span>
                  </div>
                </div>

                <button
                  onClick={handleStartPorting}
                  className="w-full mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Start Porting Process'}
                  <FiArrowRight />
                </button>
              </motion.div>

              {/* Option 2: Forward Calls */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border-2 transition-all cursor-pointer ${
                  selectedOption === 'forward' ? 'border-purple-500' : 'border-slate-700 hover:border-slate-600'
                }`}
                onClick={() => setSelectedOption('forward')}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <FiArrowRight className="text-purple-400 text-xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Option 2: Forward Calls</h3>
                      <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full">Quick Setup</span>
                    </div>
                  </div>
                  {selectedOption === 'forward' && <FiCheckCircle className="text-purple-400 text-xl" />}
                </div>
                
                <p className="text-gray-400 mb-4">
                  Keep your carrier and forward calls to platform
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <FiCheck className="text-green-400" />
                    <span className="text-gray-300">Receive calls in platform</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiCheck className="text-green-400" />
                    <span className="text-gray-300">Quick setup (minutes)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiXCircle className="text-red-400" />
                    <span className="text-gray-300">Outbound shows platform number</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiXCircle className="text-red-400" />
                    <span className="text-gray-300">No SMS from original number</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Timeline:</span>
                    <span className="text-white">Immediate</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-gray-400">Best for:</span>
                    <span className="text-white">Quick call routing</span>
                  </div>
                </div>

                <button
                  onClick={handleSetupForwarding}
                  className="w-full mt-4 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-all flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Setup Call Forwarding'}
                  <FiArrowRight />
                </button>
              </motion.div>
            </div>

            {/* Information Alert */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
              <FiInfo className="text-blue-400 mt-1 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-blue-300 font-medium mb-1">Important Information</p>
                <p className="text-gray-300">
                  Most US/CA local numbers can be ported. Toll-free numbers are also supported. 
                  SMS capabilities require 10DLC registration for compliance with carrier regulations.
                </p>
              </div>
            </div>

            {/* Existing Phone Numbers List */}
            {phoneNumbers.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-white mb-4">Your Phone Numbers</h3>
                <div className="space-y-3">
                  {phoneNumbers.map((number) => (
                    <div
                      key={number.id}
                      className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-slate-700 flex items-center justify-between"
                    >
                      <div>
                        <p className="text-white font-medium">{number.number}</p>
                        <p className="text-gray-400 text-sm">{number.friendlyName || 'Business Line'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          number.status === 'ACTIVE' 
                            ? 'bg-green-500/20 text-green-400'
                            : number.status === 'PORTING'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {number.status}
                        </span>
                        {number.capabilities?.includes('sms') && (
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">SMS</span>
                        )}
                        {number.forwardingEnabled && number.forwardingNumber && (
                          <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">
                            FWD → {number.forwardingNumber}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'search' && (
          <div className="space-y-6">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
              <FiInfo className="text-blue-400 mt-1 flex-shrink-0" />
              <div>
                <p className="text-blue-300 font-medium mb-1">Search & Purchase Phone Numbers</p>
                <p className="text-gray-300 text-sm">
                  Browse available numbers from Telnyx and provision them instantly. Numbers will be active within 1-2 minutes.
                </p>
              </div>
            </div>

            {organizationId ? (
              <NumberSearchPanel
                organizationId={organizationId}
                onSelectNumber={(number) => {
                  setSelectedNumberForProvisioning(number);
                  setShowProvisioningModal(true);
                }}
              />
            ) : (
              <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
                <p className="text-gray-400">Loading organization information...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'manage' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex items-start gap-3">
              <FiSettings className="text-green-400 mt-1 flex-shrink-0" />
              <div>
                <p className="text-white font-medium mb-1">Manage Your Phone Numbers</p>
                <p className="text-gray-300 text-sm">
                  Configure settings, assign assistants, and view usage for all your provisioned numbers.
                </p>
              </div>
            </div>

            {organizationId ? (
              <NumberManagementPanel
                organizationId={organizationId}
                onRefresh={fetchPhoneNumbers}
              />
            ) : (
              <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
                <p className="text-gray-400">Loading organization information...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'porting' && (
          <div className="space-y-6">
            {/* Porting Process Timeline */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
              <h2 className="text-xl font-semibold text-white mb-6">Number Porting Process</h2>
              
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-slate-700" />
                
                {/* Steps */}
                <div className="space-y-8">
                  {portingSteps.map((step, index) => (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 ${
                        step.status === 'completed' ? 'bg-green-500/20 border-2 border-green-500' :
                        step.status === 'in-progress' ? 'bg-blue-500/20 border-2 border-blue-500 animate-pulse' :
                        'bg-slate-700 border-2 border-slate-600'
                      }`}>
                        {step.status === 'completed' ? (
                          <FiCheck className="text-green-400 text-xl" />
                        ) : step.status === 'in-progress' ? (
                          <div className="w-3 h-3 bg-blue-400 rounded-full animate-ping" />
                        ) : (
                          <span className="text-gray-400">{step.id}</span>
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-white mb-1">{step.title}</h3>
                        <p className="text-gray-400 text-sm mb-2">{step.description}</p>
                        {step.estimatedTime && (
                          <div className="flex items-center gap-2 text-xs">
                            <FiClock className="text-gray-500" />
                            <span className="text-gray-500">{step.estimatedTime}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Required Documents */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
              <h2 className="text-xl font-semibold text-white mb-6">Required Documents</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requiredDocuments.map((doc, index) => (
                  <motion.div
                    key={doc.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-slate-900/50 rounded-lg p-4 border border-slate-700"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <HiOutlineDocument className="text-blue-400 text-lg" />
                        <h3 className="text-white font-medium">{doc.name}</h3>
                      </div>
                      {doc.required && (
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full">Required</span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm">{doc.description}</p>
                  </motion.div>
                ))}
              </div>

              <button 
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.multiple = true;
                  input.accept = '.txt,.png';
                  input.onchange = (e) => {
                    const files = (e.target as HTMLInputElement).files;
                    if (files && files.length > 0 && selectedPhoneNumber?.portingRequest?.id) {
                      const form = new FormData();
                      Array.from(files).forEach((f) => form.append('documents', f));
                      form.append('documentTypes', 'LOA');
                      form.append('documentTypes', 'BILL');
                      api.post(`/phone-numbers/porting/${selectedPhoneNumber.portingRequest.id}/documents`, form, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      }).then(() => toast.success('Documents uploaded')).catch(() => toast.error('Upload failed'));
                    } else {
                      toast.error('Select a number and start porting before uploading');
                    }
                  };
                  input.click();
                }}
                className="mt-6 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all flex items-center gap-2"
              >
                <FiUpload />
                Upload Documents
              </button>
            </div>

            {/* Post-Port Configuration */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
              <h2 className="text-xl font-semibold text-white mb-6">Post-Port Configuration</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-3">
                    <FiPhone className="text-green-400" />
                    <div>
                      <p className="text-white font-medium">Call Routing</p>
                      <p className="text-gray-400 text-sm">Configure where calls are directed</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      if (selectedPhoneNumber) {
                        navigate(`/phone-numbers/${selectedPhoneNumber.id}`);
                      } else {
                        toast.error('Please select a phone number to configure.');
                      }
                    }}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Configure →
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-3">
                    <FiMessageSquare className="text-purple-400" />
                    <div>
                      <p className="text-white font-medium">SMS/MMS Settings</p>
                      <p className="text-gray-400 text-sm">Enable texting and auto-responses</p>
                    </div>
                  </div>
                  <button 
                    onClick={async () => {
                      try {
                        if (!selectedPhoneNumber) {
                          toast.error('Select a phone number first');
                          return;
                        }
                        const next = !smsEnabled;
                        setSmsEnabled(next);
                        await api.put(`/phone-numbers/${selectedPhoneNumber.id}/settings`, { smsEnabled: next });
                        toast.success(`SMS ${next ? 'enabled' : 'disabled'}`);
                        fetchPhoneNumbers();
                      } catch {
                        toast.error('Failed to update SMS setting');
                      }
                    }}
                    className="text-2xl"
                  >
                    {smsEnabled ? (
                      <BsToggle2On className="text-green-400" />
                    ) : (
                      <BsToggle2Off className="text-gray-500" />
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-3">
                    <FiSettings className="text-orange-400" />
                    <div>
                      <p className="text-white font-medium">Call Recording</p>
                      <p className="text-gray-400 text-sm">Record calls for quality and training</p>
                    </div>
                  </div>
                  <button 
                    onClick={async () => {
                      try {
                        if (!selectedPhoneNumber) {
                          toast.error('Select a phone number first');
                          return;
                        }
                        const next = !callRecording;
                        setCallRecording(next);
                        await api.put(`/phone-numbers/${selectedPhoneNumber.id}/settings`, { recordCalls: next, transcribeCalls: next });
                        toast.success(`Recording ${next ? 'enabled' : 'disabled'}`);
                        fetchPhoneNumbers();
                      } catch {
                        toast.error('Failed to update recording');
                      }
                    }}
                    className="text-2xl"
                  >
                    {callRecording ? (
                      <BsToggle2On className="text-green-400" />
                    ) : (
                      <BsToggle2Off className="text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'forwarding' && (
          <div className="space-y-6">
            {/* Forwarding Setup Guide */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
              <h2 className="text-xl font-semibold text-white mb-6">Call Forwarding Setup</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-white mb-3">Step 1: Get a Platform Number</h3>
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <p className="text-gray-400 mb-4">
                      First, provision a new number in the platform that will receive forwarded calls.
                    </p>
                    <button 
                      onClick={handleProvisionNumber}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all flex items-center gap-2"
                    >
                      <FiPhone />
                      Get New Number
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white mb-3">Step 2: Configure at Your Carrier</h3>
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <p className="text-gray-400 mb-4">
                      Set up call forwarding from your existing number to your new platform number.
                    </p>
                    
                    <div className="space-y-3">
                      <div className="p-3 bg-slate-800 rounded-lg">
                        <p className="text-white font-medium mb-1">AT&T</p>
                        <p className="text-gray-400 text-sm">Dial *21*[platform number]# from your phone</p>
                      </div>
                      <div className="p-3 bg-slate-800 rounded-lg">
                        <p className="text-white font-medium mb-1">Verizon</p>
                        <p className="text-gray-400 text-sm">Dial *72[platform number] from your phone</p>
                      </div>
                      <div className="p-3 bg-slate-800 rounded-lg">
                        <p className="text-white font-medium mb-1">T-Mobile</p>
                        <p className="text-gray-400 text-sm">Dial **21*[platform number]# from your phone</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white mb-3">Step 3: Test & Configure</h3>
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <p className="text-gray-400 mb-4">
                      Test your forwarding setup and configure platform settings.
                    </p>
                    <div className="flex gap-3">
                      <button 
                        onClick={async () => {
                          try {
                            const current = selectedPhoneNumber || (await (async () => {
                              const pn = await handleProvisionNumber();
                              return pn;
                            })());
                            if (!current) {
                              toast.error('No phone number available to test');
                              return;
                            }
                            toast('Placing test call...', { icon: '📞' });
                            await api.post(`/phone-numbers/${current.id}/test-call`, {
                              destination: '+14155550100'
                            });
                            toast.success('Test call initiated');
                          } catch (e) {
                            console.error('Test call failed', e);
                            toast.error('Failed to initiate test call');
                          }
                        }}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all flex items-center gap-2"
                      >
                        <FiPhone />
                        Test Call
                      </button>
                      <button 
                        onClick={() => {
                          if (selectedPhoneNumber) {
                            navigate(`/phone-numbers/${selectedPhoneNumber.id}`);
                          } else {
                            toast.error('Please select a phone number to configure.');
                          }
                        }}
                        className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-all flex items-center gap-2"
                      >
                        <FiSettings />
                        Configure Settings
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Limitations Alert */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <FiAlertCircle className="text-yellow-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-yellow-300 font-medium mb-2">Forwarding Limitations</p>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>• Outbound calls will show platform number, not your original</li>
                    <li>• Cannot send/receive SMS from original number in platform</li>
                    <li>• Caller ID may show platform number to recipients</li>
                    <li>• Some advanced features may be limited</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'compliance' && (
          <div className="space-y-6">
            {/* Australian Regulatory Compliance */}
            <AustralianCompliancePanel organizationId={organizationId} />

            {/* 10DLC Registration */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">US 10DLC Registration & Compliance</h2>
                <span className="text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full">Required for US SMS</span>
              </div>

              <p className="text-gray-400 mb-6">
                To send A2P (Application-to-Person) SMS/MMS from US local numbers, carrier registration is required.
              </p>

              <div className="space-y-4">
                {complianceRequirements.map((req, index) => (
                  <motion.div
                    key={req.type}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        req.status === 'completed' ? 'bg-green-500/20' : 'bg-slate-700'
                      }`}>
                        {req.status === 'completed' ? (
                          <FiCheck className="text-green-400" />
                        ) : (
                          <span className="text-gray-400 text-sm">{index + 1}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">{req.type}</p>
                        <p className="text-gray-400 text-sm">{req.description}</p>
                      </div>
                    </div>
                    {req.status === 'pending' && (
                      <button className="text-blue-400 hover:text-blue-300 transition-colors text-sm">
                        Complete →
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Compliance Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                <FiShield className="text-green-400 text-2xl mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Carrier Trust</h3>
                <p className="text-gray-400 text-sm">
                  Registered numbers have higher deliverability rates and are less likely to be marked as spam.
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                <FiGlobe className="text-blue-400 text-2xl mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Higher Throughput</h3>
                <p className="text-gray-400 text-sm">
                  Verified campaigns can send more messages per second with better delivery rates.
                </p>
              </div>
            </div>

            {/* Cost Information */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Registration Costs</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Brand Registration</span>
                    <span className="text-white font-medium">$4/month</span>
                  </div>
                  <p className="text-xs text-gray-500">One-time $40 vetting fee</p>
                </div>

                <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Campaign Creation</span>
                    <span className="text-white font-medium">$15/month</span>
                  </div>
                  <p className="text-xs text-gray-500">Per use case</p>
                </div>

                <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Number Association</span>
                    <span className="text-white font-medium">Free</span>
                  </div>
                  <p className="text-xs text-gray-500">Link to campaigns</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button 
                onClick={() => {
                  const brandData = {
                    brandName: 'Your Company Name',
                    ein: '12-3456789',
                    brandType: 'PRIVATE_PROFIT',
                    website: 'https://yourcompany.com',
                    vertical: 'PROFESSIONAL'
                  };
                  handleCreateBrand(brandData);
                }}
                className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all flex items-center gap-2"
              >
                <FiFileText />
                Start 10DLC Registration
              </button>
              <button 
                onClick={() => {
                  toast('Downloading compliance guide...');
                  // Create a mock PDF download
                  const link = document.createElement('a');
                  link.href = 'data:text/plain,10DLC%20Compliance%20Guide%0A%0A1.%20Register%20your%20brand%0A2.%20Create%20campaigns%0A3.%20Associate%20phone%20numbers';
                  link.download = '10DLC_Compliance_Guide.txt';
                  link.click();
                }}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all flex items-center gap-2"
              >
                <FiDownload />
                Download Compliance Guide
              </button>
              <button 
                onClick={() => {
                  window.open('https://www.10dlc.org/', '_blank');
                }}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all flex items-center gap-2"
              >
                <FiExternalLink />
                Learn More
              </button>
            </div>
          </div>
        )}

        {/* Provisioning Modal */}
        <ProvisioningModal
          isOpen={showProvisioningModal}
          onClose={() => {
            setShowProvisioningModal(false);
            setSelectedNumberForProvisioning(null);
          }}
          organizationId={organizationId}
          selectedNumber={selectedNumberForProvisioning}
          onSuccess={() => {
            fetchPhoneNumbers();
            setActiveTab('manage');
          }}
        />
      </div>
    </div>
  );
};

export default PhoneNumbersPage;